import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

const api = axios.create({
  baseURL: API_URL,
  timeout: 60000, // 60 секунд для обработки изображений
  headers: {
    'Content-Type': 'multipart/form-data',
  },
})

// Обработка ошибок
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.code === 'ECONNABORTED') {
      error.message = 'Превышено время ожидания. Попробуйте еще раз.'
    } else if (error.message === 'Network Error') {
      error.message = 'Не удалось подключиться к серверу. Убедитесь, что бэкенд запущен.'
    }
    return Promise.reject(error)
  }
)

export interface UploadResponse {
  success: boolean
  filename: string
  size: number
}

export const uploadFile = async (file: File): Promise<UploadResponse> => {
  const formData = new FormData()
  formData.append('file', file)
  
  const response = await api.post<UploadResponse>('/api/upload', formData)
  return response.data
}

export const removeBackground = async (file: File): Promise<Blob> => {
  const formData = new FormData()
  formData.append('file', file)
  
  try {
    const response = await api.post('/api/remove-bg', formData, {
      responseType: 'blob',
      timeout: 60000, // 60 секунд
    })
    
    // Проверяем, что это действительно изображение
    if (response.data.type && response.data.type.startsWith('image/')) {
      return response.data
    }
    
    // Если это ошибка в формате JSON
    const text = await response.data.text()
    try {
      const error = JSON.parse(text)
      throw new Error(error.detail || 'Ошибка при обработке изображения')
    } catch {
      throw new Error('Получен некорректный ответ от сервера')
    }
  } catch (error: any) {
    if (error.response?.data) {
      // Пытаемся прочитать ошибку из blob
      const text = await error.response.data.text()
      try {
        const errorData = JSON.parse(text)
        throw new Error(errorData.detail || 'Ошибка при обработке')
      } catch {
        throw new Error(text || 'Ошибка при обработке изображения')
      }
    }
    throw error
  }
}

export const createPhotosession = async (reference: File, style: string = 'studio', customerId?: string): Promise<Blob> => {
  const formData = new FormData()
  formData.append('reference', reference)
  formData.append('style', style)
  
  // Формируем URL с query параметрами
  let url = '/api/photosession'
  if (customerId) {
    url += `?customer_id=${encodeURIComponent(customerId)}`
  }
  
  try {
    const response = await api.post(url, formData, {
      responseType: 'blob',
      timeout: 300000, // 5 минут для генерации
    })
    
    // Проверяем, что это действительно изображение
    if (response.data.type && response.data.type.startsWith('image/')) {
      return response.data
    }
    
    // Если это ошибка в формате JSON
    const text = await response.data.text()
    try {
      const error = JSON.parse(text)
      throw new Error(error.detail || 'Ошибка при генерации фотосессии')
    } catch {
      throw new Error('Получен некорректный ответ от сервера')
    }
  } catch (error: any) {
    if (error.response?.data) {
      // Пытаемся прочитать ошибку из blob
      const text = await error.response.data.text()
      try {
        const errorData = JSON.parse(text)
        throw new Error(errorData.detail || 'Ошибка при генерации')
      } catch {
        throw new Error(text || 'Ошибка при генерации фотосессии')
      }
    }
    throw error
  }
}

export const getPhotosessionStyles = async () => {
  const response = await api.get('/api/photosession/styles')
  return response.data
}

export const virtualTryOn = async (person: File, clothing: File) => {
  const formData = new FormData()
  formData.append('person', person)
  formData.append('clothing', clothing)
  
  const response = await api.post('/api/try-on', formData)
  return response.data
}

export const createVideo = async (images: File[]) => {
  const formData = new FormData()
  images.forEach((img) => {
    formData.append('images', img)
  })
  
  const response = await api.post('/api/video', formData)
  return response.data
}

export default api

